import projectTemplatesService from '../services/projectTemplatesService';
import dataModel from '../models/DataModel';
import OfflineQueue from './queue/OfflineQueue';
import DAO from './DAO.js';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';

export const project_type_root = {
  project_type: {

    // Children are defined in this manner
    project_templates: {
      markets: { },
    },
  },
};

export const project_type_attributes_filter = {
  project_type: { },
};

export default class ProjectTypesDAO extends DAO {

  /*************************************************************
   *
   *  Initialization
   *
   *************************************************************/

  constructor(store) {
    super(store, 'project_type');
    this.OfflineQueue = OfflineQueue;

    this.unsetProjectType = this.unsetProjectType.bind(this);
    this.clearProjectTypes = this.clearProjectTypes.bind(this);
  }

  unsetProjectType(projectTypeId) {
    StoreItemProcessing.deleteItemInStore(this.itemType, projectTypeId);
  }

  clearProjectTypes() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }
 
  /**
   * @param  {number} projectTypeId - project template record identifier
   * @param  {object <boolean>} options.force - forces the get to happen
   * @param  {object <object>} options.recordDescriptor - used to determine completeness of store object and to denormalize
   *                                                      returned project type
   * 
   * @returns {object<Promise<projectType>>} projectType - denormalized project type
   */
  getProjectType(projectTypeId, options) {
    return this._getItem(projectTypeId, projectTemplatesService.getProjectType.bind(this, projectTypeId, null, options), options, this.mapToReturn);
  }

  // TODO: how do we handle other operations on the placeholder object?
  // Might almost be a good idea to store the temp_uuid in the db so that requests can use either

  /**
  * @param  {object <projectType>} projectType - denormalized project template to create
  * @param  {object <object>} options.recordDescriptor - used to format returned project template
  * 
  * @returns {object<Promise<projectType>>} projectType - denormalized project template
  */
  createProjectType(projectType, options) {

    return projectTemplatesService.createProjectType(projectType).then(response => {
      if (response.error) {
        const errResponse = response.error;
        if(errResponse && errResponse.response && typeof errResponse.response.data === 'object'){
          const errorMessage = Object.entries(errResponse.response.data).map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${key} ${value.join(', ')}`;
            } else {
              return `${key} ${value}`;
            }
          }).join('\n');
          dataModel.displayHTTPError('Creating a project type: \n', errorMessage, false);
        } else {
          dataModel.displayHTTPError('Creating a project type', response.error, false);
        }
        this.throwItemError('Error Creating a project type', response);
      }
      else {
        this.normalizeAndSyncToStore(response);
        return this.mapToReturn(this.findItem(response.data.id), options);
      }
    }).catch(error => {
      throw error;
    });
  }

  /**
  * @param  {object <projectType>} projectType - denormalized project template to update
  * @param  {object <object>} options.recordDescriptor - used to format returned project template
  * 
  * @returns {object<Promise<projectType>>} projectType - denormalized project template
  */
  async updateProjectType(projectType, options) {

    const normalizedProjectType = this.normalizeItem(projectType);
    this.markPending(normalizedProjectType);
    const denormalizedProjectType = this.mapToReturn(normalizedProjectType, options);

    if (dataModel.isOffline) {
      this.markDirty(denormalizedProjectType, true);
      this.OfflineQueue.addQueueItem('update', { projectType: denormalizedProjectType });
      this.throwItemError(); //make sure to send the denormalized build
      // return Promise.resolve(denormalizedBuild)
    }
    else {
      this.markPending(normalizedProjectType);

      return projectTemplatesService.updateProjectType(normalizedProjectType, null, options).then(response => {
        if (response.error && !response.offline) {
          dataModel.displayHTTPError('Updating a project type', response.error, false);
          this.markError(denormalizedProjectType);
          this.throwItemError(); //make sure to send the denormalized build
          // return Promise.resolve(denormalizedBuild)
        }
        else if (response.offline) {
          this.markDirty(denormalizedProjectType, true);
          this.OfflineQueue.addQueueItem(this.itemType, 'update', { projectType: denormalizedProjectType });
          this.throwItemError(); //make sure to send the denormalized build
          // return Promise.resolve(denormalizedBuild)
        }
        else {
          this.markDirty(normalizedProjectType, false);
          this.normalizeAndSyncToStore(response);
          let updatedProjectType = this.findItem(projectType.id);
          if (updatedProjectType) {
            // TODO: what is this supposed to do?
            // projectTypesService.migrateProjectProjectType(updatedProjectType, updatedProjectType)
          }
          return this.mapToReturn(this.findItem(projectType.id), options);
        }
      })
        .catch((error) => {
          throw error;
        });
    }
  }

  /**
  * @param  {number} projectTypeId - project template record identifier
  * 
  * @returns {object<Promise<string>>} result - "offline", "error", "success"
  */
  deleteProjectType(projectTypeId) {
    let projectType = this.findItem(projectTypeId);
    if (projectType) {
      this.unsetProjectType(projectTypeId);
    }

    // TODO: action cable
    if (dataModel.isOffline) {
      this.OfflineQueue.addQueueItem(this.itemType, 'delete', { projectTypeId });
      return Promise.resolve('offline');
    }
    return projectTemplatesService.deleteProjectType(projectTypeId).then(response => {
      if (response.error && !response.offline) {
        dataModel.displayHTTPError('Deleting a project type', response.error, false);

        StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(projectType));
        this.throwItemError();
      }
      else if (response.offline) {
        this.OfflineQueue.addQueueItem(this.itemType, 'delete', { projectTypeId });
        this.throwItemError();
      }
      else {
        dataModel.displayInfoErrorMessage('The project type was successfully deleted.', 4000, 1);
        return this.mapToReturn(projectType);
      }
    });
  }

  /**
  * @param  {number} projectTypeId - project template record identifier
  * 
  * @returns {object<Promise<string>>} result - "offline", "error", "success"
  */
  archive(projectTypeId) {
    let projectType = this.findItem(projectTypeId);
    if (projectType) {
      this.unsetProjectType(projectTypeId);
    }

    // TODO: action cable
    if (dataModel.isOffline) {
      this.markDirty(projectType, true);
      this.OfflineQueue.addQueueItem(this.itemType, 'archive', { projectTypeId });
      return Promise.resolve('offline');
    }
    return projectTemplatesService.archiveProjectType(projectTypeId).then(response => {
      if (response.error && !response.offline) {
        dataModel.displayHTTPError('Archiving a project type', response.error, false);

        StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(projectType));
        this.throwItemError();
      }
      else if (response.offline) {
        this.markDirty(projectType, true);
        this.OfflineQueue.addQueueItem(this.itemType, 'archive', { projectTypeId });
        this.throwItemError(); //make sure to send the denormalized build
        // return 'offline'
      }
      else {
        this.markDirty(projectType, false);
        dataModel.displayInfoErrorMessage('The project type was successfully archived.', 4000, 1);
        return this.mapToReturn(projectType);
      }
    });  
  }

  /** Util Functions */
  completeOfflineUpdate(queueItem) {
    switch (queueItem.operationKey) {
    case 'create':
      return this.createProjectType(queueItem.itemInfo.projectType);
    case 'update':
      return this.updateProjectType(queueItem.itemInfo.projectType);
    case 'delete':
      return this.deleteProjectType(queueItem.itemInfo.projectTypeId);
    case 'archive':
      return this.archive(queueItem.itemInfo.projectTypeId);
    default:
      break;
    }
  }

  handleActionCableAndGetRequest(acData) {
    const projectType = this.findItem(acData.project_type_id);
    if (projectType) {
      if (acData.is_attribute_update) {
        this.markNeedsAttributeSync(projectType);
      }
      else {
        this.markNeedsSync(projectType);
      }
    }
    if (acData.is_attribute_update) {
      // TODO
      // options.filter = SOME_ATTRIBUTE_FILTER
    }
    switch (acData.type) {
    case 'newProjectType':
      return this.getProjectType.bind(this);
    case 'updateProjectType':
      return this.getProjectType.bind(this);
    case 'deleteProjectType':
      const deletedType = StoreItemProcessing.deleteItemInStore(this.itemType, acData.project_type_id);
      return Promise.resolve(deletedType);
    default:
    }
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_project_types, options.toIgnore.project_types, this.unsetProjectType, this.clearProjectTypes, options);
  }
}



// WEBPACK FOOTER //
// ./src/dao/ProjectTypesDAO.js